import type { Dispatch, SetStateAction } from "react";
import { CargoDimensionMapper } from "../cargoDimensions";
import { LIMITS } from "../constants";
import type { ExtractFillMap } from "../extractFill";
import { makeCargoLine } from "../formModel";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { CargoLine, FormState, PalletType } from "../types";
import type { Errors } from "../validation";
import { SelectInput, TextInput } from "./fields";
import { CloseIcon } from "./icons";
import { QtyStepper } from "./QtyStepper";

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  errors: Errors;
  extractFill?: ExtractFillMap;
  palletTypes: PalletType[];
  onBlur?: (id: string) => void;
}

const dimensions = new CargoDimensionMapper();

export function CargoLines({ form, setForm, errors, extractFill, palletTypes, onBlur }: Props) {
  const copy = useQuoteCopy();
  const updateLine = (id: number, next: CargoLine) => {
    setForm((f) => ({
      ...f,
      cargo: f.cargo.map((l) => (l.id === id ? next : l)),
    }));
  };

  const removeLine = (id: number) => {
    setForm((f) => {
      const remaining = f.cargo.filter((l) => l.id !== id);
      return { ...f, cargo: remaining.length ? remaining : [makeCargoLine()] };
    });
  };

  const addLine = () => {
    setForm((f) => ({ ...f, cargo: [...f.cargo, makeCargoLine()] }));
  };

  return (
    <>
      <div className="nv-ship-table">
      <div className="nv-ship-head">
        <span className="nv-ship-col-qty"><span className="nv-ship-col-label">{copy.t("shipment.qty")}</span></span>
        <span className="nv-ship-col-type"><span className="nv-ship-col-label">{copy.t("shipment.type")}</span></span>
        <span className="nv-ship-col-dims"><span className="nv-ship-col-label">{copy.t("shipment.dims")}</span></span>
        <span className="nv-ship-col-stack"><span className="nv-ship-col-label">{copy.t("shipment.stackable")}</span></span>
        {form.cargo.length > 1 && <span className="nv-ship-head-action" />}
      </div>

      {form.cargo.map((line, idx) => {
        const lineNo = idx + 1;
        const name = (text: string) => copy.t("shipment.lineLabel", { text, n: lineNo });
        const isCustom = line.type === "custom";
        const dimOptions = dimensions.options(line.type, line.height);
        const sizeLocked = CargoDimensionMapper.isSizeLocked(line.type);
        const ldmOnlyType = CargoDimensionMapper.isLdmOnlyType(line.type);

        return (
          <div className="nv-ship-row" key={line.id}>
            <QtyStepper
              id={`nv-cargo-${line.id}-qty`}
              label={copy.t("shipment.qty")}
              aria-label={name(copy.t("shipment.quantity"))}
              error={errors[`cargo-${line.id}-qty`]}
              fillStatus={extractFill?.status(`cargo-${line.id}-qty`)}
              value={line.qty}
              min={LIMITS.qty.min}
              max={LIMITS.qty.max}
              onChange={(v) => updateLine(line.id, { ...line, qty: v })}
              onBlur={() => onBlur?.(`cargo-${line.id}-qty`)}
            />

            <SelectInput
              id={`nv-cargo-${line.id}-type`}
              className="nv-ship-type"
              label={copy.t("shipment.type")}
              aria-label={name(copy.t("shipment.type"))}
              error={errors[`cargo-${line.id}-type`]}
              fillStatus={extractFill?.status(`cargo-${line.id}-type`)}
              value={line.type}
              onChange={(v) => {
                if (!CargoDimensionMapper.isPalletType(v)) return;
                updateLine(line.id, dimensions.applyType(line, v));
              }}
            >
              {palletTypes.map((type) => (
                <option value={type} key={type}>
                  {copy.palletLabel(type)}
                </option>
              ))}
            </SelectInput>

            <div className="nv-ship-dims">
              <span className="nv-ship-dims-label">{copy.t("shipment.dims")}</span>
              {sizeLocked ? (
                <span className="nv-sr-only">{copy.t("shipment.dimsNa")}</span>
              ) : (
              <>
              {!ldmOnlyType && (
              <SelectInput
                id={`nv-cargo-${line.id}-sizeMode`}
                className="nv-ship-size-mode"
                aria-label={name(copy.t("shipment.size"))}
                fillStatus={extractFill?.status(`cargo-${line.id}-sizeMode`)}
                value={line.sizeMode}
                onChange={(v) => {
                  if (!CargoDimensionMapper.isSizeMode(v)) return;
                  updateLine(line.id, dimensions.applySizeMode(line, v));
                }}
              >
                <option value="dimensions">{copy.t("shipment.sizeModeDims")}</option>
                <option value="ldm">{copy.t("shipment.sizeModeLdm")}</option>
              </SelectInput>
              )}

              {ldmOnlyType || line.sizeMode === "ldm" ? (
                <TextInput
                  id={`nv-cargo-${line.id}-ldm`}
                  className="nv-ship-ldm"
                  aria-label={name(copy.t("shipment.ldm"))}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={LIMITS.ldm.min}
                  max={LIMITS.ldm.max}
                  placeholder="0.00"
                  suffix="LDM"
                  error={errors[`cargo-${line.id}-ldm`]}
                  fillStatus={extractFill?.status(`cargo-${line.id}-ldm`)}
                  value={line.ldm}
                  onChange={(v) => updateLine(line.id, dimensions.applyLdm(line, v))}
                  onBlur={() => onBlur?.(`cargo-${line.id}-ldm`)}
                />
              ) : isCustom ? (
                <>
                  <TextInput
                    id={`nv-cargo-${line.id}-length`}
                    className="nv-ship-dim-input"
                    aria-label={name(copy.t("shipment.lengthCm"))}
                    type="number"
                    inputMode="numeric"
                    min={LIMITS.lengthCm.min}
                    max={LIMITS.lengthCm.max}
                    error={errors[`cargo-${line.id}-length`]}
                    fillStatus={extractFill?.status(`cargo-${line.id}-length`)}
                    value={line.length}
                    onChange={(v) => updateLine(line.id, dimensions.applyLength(line, v))}
                    onBlur={() => onBlur?.(`cargo-${line.id}-length`)}
                  />
                  <span className="dim-x" aria-hidden="true">
                    &times;
                  </span>
                  <TextInput
                    id={`nv-cargo-${line.id}-width`}
                    className="nv-ship-dim-input"
                    aria-label={name(copy.t("shipment.widthCm"))}
                    type="number"
                    inputMode="numeric"
                    min={LIMITS.widthCm.min}
                    max={LIMITS.widthCm.max}
                    error={errors[`cargo-${line.id}-width`]}
                    fillStatus={extractFill?.status(`cargo-${line.id}-width`)}
                    value={line.width}
                    onChange={(v) => updateLine(line.id, dimensions.applyWidth(line, v))}
                    onBlur={() => onBlur?.(`cargo-${line.id}-width`)}
                  />
                  <span className="dim-x" aria-hidden="true">
                    &times;
                  </span>
                  <TextInput
                    id={`nv-cargo-${line.id}-height`}
                    className="nv-ship-dim-input"
                    aria-label={name(copy.t("shipment.heightCm"))}
                    type="number"
                    inputMode="numeric"
                    min={LIMITS.heightCm.min}
                    max={LIMITS.heightCm.max}
                    error={errors[`cargo-${line.id}-height`]}
                    fillStatus={extractFill?.status(`cargo-${line.id}-height`)}
                    value={line.height}
                    onChange={(v) => updateLine(line.id, dimensions.applyHeight(line, v))}
                    onBlur={() => onBlur?.(`cargo-${line.id}-height`)}
                  />
                </>
              ) : (
                <SelectInput
                  id={`nv-cargo-${line.id}-height`}
                  className="nv-ship-dim-select"
                  aria-label={name(copy.t("shipment.dimensions"))}
                  fillStatus={extractFill?.status(`cargo-${line.id}-height`)}
                  value={line.height}
                  onChange={(v) => updateLine(line.id, { ...line, height: v })}
                  onBlur={() => onBlur?.(`cargo-${line.id}-height`)}
                >
                  <option value="">{copy.t("shipment.selectDims")}</option>
                  {dimOptions.map((opt) => (
                    <option value={opt.value} key={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </SelectInput>
              )}
              </>
              )}
            </div>

            <SelectInput
              id={`nv-cargo-${line.id}-stack`}
              className="nv-ship-stack"
              label={copy.t("shipment.stackable")}
              aria-label={name(copy.t("shipment.stackable"))}
              fillStatus={extractFill?.status(`cargo-${line.id}-stack`)}
              value={line.stack ? "yes" : "no"}
              onChange={(v) => updateLine(line.id, { ...line, stack: v === "yes" })}
            >
              <option value="no">{copy.t("common.no")}</option>
              <option value="yes">{copy.t("common.yes")}</option>
            </SelectInput>

            {form.cargo.length > 1 && (
              <button
                type="button"
                className="remove-line"
                title={copy.t("shipment.removeRow", { n: lineNo })}
                aria-label={copy.t("shipment.removeRow", { n: lineNo })}
                onClick={() => removeLine(line.id)}
              >
                <CloseIcon size={16} />
              </button>
            )}
          </div>
        );
      })}
      </div>

      <div className="nv-ship-foot">
        <button type="button" className="nv-add-ship-row" onClick={addLine}>
          + {copy.t("shipment.addRow")}
        </button>
        <div className="nv-ship-totals">
          <span>{copy.t("shipment.totalLines", { count: form.cargo.length })}</span>
        </div>
      </div>
    </>
  );
}
